import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NgxDropzoneModule, NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ClipboardModule } from 'ngx-clipboard';
import { S3Upload } from 'src/app/common/utils/S3Upload';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CampaignsCampaignAdminApiClient, MediaObject } from 'src/app/api/api-campaigns.service.generated';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { ButtonComponent } from "wecheer-common";
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-campaign-media',
  templateUrl: './campaign-media.component.html',
  styleUrls: ['./campaign-media.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FlexLayoutModule,
    NgxDropzoneModule,
    NgxDatatableModule,
    ClipboardModule,
    DatePipe,
    ButtonComponent
  ]
})
export class CampaignMediaComponent implements OnInit {
  private capaignsApi = inject(CampaignsCampaignAdminApiClient);
  private snackBar = inject(MatSnackBar);

  @Input() campaignId: string;
  files: File[] = [];
  mediaOjects: MediaObject[];
  ColumnMode = ColumnMode;
  readonly MAX_SIZE_BYTES: number = 10485760;
  readonly headerHeight = 50;
  readonly rowHeight = 60;

  ngOnInit() {
    this.getMediaItems();
  }

  onDropzoneSelect(event: NgxDropzoneChangeEvent) {
    event.addedFiles.forEach(element => {
      this.files.push(element);
      this.processFile(element);
    });
  }

  dropzoneRemove(file: File) {
    this.files.splice(this.files.indexOf(file), 1);
  }

  images(): File[] {
    return this.files.filter(file => file.type.startsWith("image/"));
  }

  videos(): File[] {
    return this.files.filter(file => file.type.startsWith("video/"));
  }

  others(): File[] {
    return this.files.filter(file => !file.type.startsWith("image/") && !file.type.startsWith("video/"));
  }

  isValidFileType(file: File): boolean {
    return file.type.includes("application/json") || this.isFontFile(file) || file.type.startsWith("image/") || file.type.startsWith("video/");
  }

  isValidSize(file: File): boolean {
    return file.size && this.MAX_SIZE_BYTES >= file.size;
  }

  getCleanKey(key: string) {
    return key.split('/').pop()
  }

  getMediaItems() {
    this.capaignsApi.listMedia(this.campaignId).subscribe(result => this.mediaOjects = result);
  }

  async processFile(file: File) {
    try {
      if (!file) {
        this.dropzoneRemove(file);
        this.snackBar.open(`Invalid file ${file.name}.`, 'Dismiss', { duration: 2000 });
        return;
      }

      if (!this.isValidFileType(file)) {
        this.dropzoneRemove(file);
        this.snackBar.open(`Invalid file ${file.name}. Type not supported.`, 'Dismiss', { duration: 2000 });
        return;
      }

      if (!this.isValidSize(file)) {
        this.dropzoneRemove(file);
        this.snackBar.open(`Invalid file ${file.name}. Size is too big.`, 'Dismiss', { duration: 2000 });
        return;
      }

      const fileName = file?.name?.replace(/[&\/\\#^+()$~%'":*?<>{}!@]/g, '').replace(/\s+/g, '-');
      const mediaUploadKey = `${GUID.newGuid().replace(/-/g, '')}_${fileName}`;
      const uploadUrl = await lastValueFrom(this.capaignsApi.getMediaUploadUrl(this.campaignId, mediaUploadKey));

      const metadata: Map<string, string> = new Map([
        ['campaign-id', this.campaignId]
      ]);

      const uploadSuccess = await lastValueFrom(S3Upload.uploadWithMetadata(uploadUrl, file, metadata));
      if (uploadSuccess) {
        this.dropzoneRemove(file);
      }
      else {
        this.snackBar.open(`Upload failed for file ${file.name}`, 'Dismiss', { duration: 2000 });
        this.dropzoneRemove(file);
      }
    } catch (err) {
      this.snackBar.open(`Upload failed for file ${file.name}`, 'Dismiss', { duration: 2000 });
      this.dropzoneRemove(file);
    }

    if (this.files.length == 0) {
      this.getMediaItems();
    }
  }

  private isFontFile(file: File) {
    if (!file) {
      return false;
    }
    if (file.type?.startsWith("font/")) {
      return true;
    }

    const fileName = file.name;
    const fontExtensions = ['.otf', '.ttf', '.woff', '.woff2', '.eot'];
    const fileExtension = fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2);

    return fontExtensions.includes('.' + fileExtension.toLowerCase());
  }
}
